/**
 * @file Source-section selector for one mapping-review table slice. A slice may
 * resolve to several source chunks (one table split across pages, sheets, or
 * markdown sections); this `Select` switches the slice's in-view chunk
 * (`activeChunkId`, `null` = all sections combined) and shows the active
 * chunk's provenance subtitle. Shared by both review surfaces — rendered
 * section-level for an entity-records import (`MappingReviewSections`) and
 * per-table for a whole document (`MappingReviewTables`), so the source
 * selector sits in one consistent slot. User copy routes through
 * `mappingReviewLabels` ("section" / "All sections (combined)"); the Select is
 * disabled while an approval is being written (`useMappingReviewSubmitting`) or
 * when the slice has a single chunk. `describeSource` is the one provenance
 * renderer for a `ChunkSource`, exported for the table-identity header.
 * The Select is 350px wide, capped at the panel width so a phone never clips it.
 */
import { FC } from 'react';
import { Select, Typography, theme } from 'antd';
import { useSnapshot } from 'valtio';
import FlexCol from '../../atoms/FlexCol';
import type { MappingReviewTableSlice } from './mappingReviewState';
import { mappingReviewLabels } from './mappingReviewLabels';
import { mappingReviewDescribeSource } from './mappingReviewDescribeSource';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';

const { Text } = Typography;

const MappingReviewChunkNav: FC<{ slice: MappingReviewTableSlice }> = ({
  slice,
}) => {
  const { token } = theme.useToken();
  const snap = useSnapshot(slice);
  const submitting = useMappingReviewSubmitting();
  const chunks = snap.chunks;
  if (chunks.length === 0) return null;

  const options = [
    ...(chunks.length > 1
      ? [
          {
            value: '',
            label: mappingReviewLabels.allSections,
            description: `${chunks.length} ${mappingReviewLabels.section}s · ${chunks.reduce((n, c) => n + c.rows.length, 0)} rows`,
          },
        ]
      : []),
    ...chunks.map((c) => ({
      value: c.label.id,
      label: c.label.description,
      description: mappingReviewDescribeSource(c.source),
    })),
  ];
  const active =
    snap.activeChunkId ?? (chunks.length === 1 ? chunks[0].label.id : '');
  const activeChunk = chunks.find((c) => c.label.id === active);
  const provenance = activeChunk
    ? mappingReviewDescribeSource(activeChunk.source)
    : (options[0]?.description ?? '');

  return (
    <FlexCol noGap noGrow style={{ maxWidth: '100%' }}>
      <Select
        size="small"
        aria-label="Select table section"
        value={active}
        onChange={(value: string) => {
          slice.activeChunkId = value === '' ? null : value;
        }}
        options={options}
        optionRender={(option) => (
          <FlexCol noGap>
            <Text>{option.data.label}</Text>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {option.data.description}
            </Text>
          </FlexCol>
        )}
        style={{ width: 350, maxWidth: '100%' }}
        disabled={submitting || chunks.length === 1}
      />
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {provenance}
      </Text>
    </FlexCol>
  );
};

export default MappingReviewChunkNav;
